import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import {
  ADDRESS_MAP_OPTIONS,
  ADDRESS_MAP_ZOOM,
  GOOGLE_MAPS_BROWSER_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
} from "../utils/googleMaps";
import { WarningIcon } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import type { AddressValue } from "../types";
import { validateAddress, type PlacesHint } from "../api";
import {
  isAddressUnplaceable,
  UNPLACEABLE_ADDRESS_TOOLTIP,
} from "../utils/address";

export interface AddressInputHandle {
  // Flush any in-flight or pending validation and resolve to the AddressValue
  // that onChange was (or will be) called with. Callers that need to commit a
  // draft value — e.g. the portalled RMS cell editor on Tab / outside click —
  // await this before reading the committed value. Returns null when there's
  // nothing pending and no new text to validate.
  flushPending: () => Promise<AddressValue | null>;
}

interface AddressInputProps {
  value: AddressValue | string | null;
  onChange: (value: AddressValue) => void;
  placeholder?: string;
  className?: string;
  // When true, suggestions include cities/regions, not just street addresses.
  allowGeneral?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

function addressValueOf(
  value: AddressInputProps["value"]
): AddressValue | null {
  if (value == null) return null;
  if (typeof value === "string") {
    return {
      formatted: value,
      validated: false,
    };
  }
  return value;
}

const MAP_STYLE: React.CSSProperties = {
  width: "100%",
  height: "200px",
  borderRadius: "6px",
  marginTop: "8px",
};

const AddressInput = forwardRef<AddressInputHandle, AddressInputProps>(
  (
    {
      value,
      onChange,
      placeholder = "Enter an address",
      className = "",
      allowGeneral = false,
      inputRef,
    },
    ref
  ) => {
    const { isLoaded } = useJsApiLoader({
      id: GOOGLE_MAPS_SCRIPT_ID,
      googleMapsApiKey: GOOGLE_MAPS_BROWSER_API_KEY,
      libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const internalRef = useRef<HTMLInputElement>(null);
    const elementRef = inputRef ?? internalRef;
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(
      null
    );

    const initial = addressValueOf(value);
    const [text, setText] = useState<string>(initial?.formatted ?? "");
    // Separate display override set when the user picks a Google suggestion.
    // Doesn't participate in handleBlur / flushPending / commit invariants
    // (those still read `text` / `textRef`), so the commit path keeps using
    // the user-typed value, while the input displays the picked suggestion
    // immediately. Cleared when the validated value prop catches up.
    const [previewText, setPreviewText] = useState<string | null>(null);
    const [pending, setPending] = useState<boolean>(false);
    // Tracks the in-flight validation promise so external callers (the
    // portalled cell editor) can await it via the imperative handle before
    // committing the cell value.
    const pendingPromiseRef = useRef<Promise<AddressValue> | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const textRef = useRef(text);
    textRef.current = text;
    const initialFormattedRef = useRef(initial?.formatted ?? "");
    initialFormattedRef.current = initial?.formatted ?? "";
    // The Places Autocomplete listener is attached once and holds a stale
    // `value` closure across prop updates. Mirror the current committed
    // AddressValue into a ref so the listener can compare the picked
    // suggestion's place_id against the live fact instead of whatever
    // value was on mount.
    const currentValueRef = useRef<AddressValue | null>(initial);
    currentValueRef.current = initial;

    useEffect(() => {
      setText(addressValueOf(value)?.formatted ?? "");
      // Validation committed a value back up to us — drop the picked-suggestion
      // preview so the input reflects the canonical formatted address.
      setPreviewText(null);
    }, [value]);

    const runValidation = (
      raw: string,
      hint?: PlacesHint
    ): Promise<AddressValue> => {
      setPending(true);
      const promise = validateAddress(raw, hint)
        .catch((): AddressValue => ({ formatted: raw, validated: false }))
        .then((v) => {
          onChangeRef.current(v);
          return v;
        })
        .finally(() => {
          setPending(false);
          if (pendingPromiseRef.current === promise) {
            pendingPromiseRef.current = null;
          }
        });
      pendingPromiseRef.current = promise;
      return promise;
    };

    useEffect(() => {
      if (!isLoaded || !elementRef.current) return;
      if (autocompleteRef.current) return;

      const ac = new google.maps.places.Autocomplete(elementRef.current, {
        // "geocode" includes street addresses + cities/regions; "address" restricts
        // to precise street addresses only.
        types: allowGeneral ? ["geocode"] : ["address"],
        fields: [
          "formatted_address",
          "geometry",
          "place_id",
          "address_components",
        ],
      });
      autocompleteRef.current = ac;

      const listener = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const formatted =
          place.formatted_address || elementRef.current?.value || "";
        if (!formatted) return;
        // Picked the same place that's already on the fact (Google's two
        // canonical forms from Places Autocomplete and Address Validation
        // can render slightly differently — "Ave" vs "Avenue" — even
        // though the underlying place_id matches). Skip re-validation so
        // the field doesn't flip to dirty for a no-op round-trip that
        // would also hide the Source chip.
        const currentPlaceId = currentValueRef.current?.place_id;
        if (
          place.place_id &&
          currentPlaceId &&
          place.place_id === currentPlaceId
        ) {
          return;
        }
        // Display the picked suggestion immediately while validation is in
        // flight. A plain setText would invalidate the text/initial/blur
        // invariants (e.g. handleBlur reads initial.formatted) and wreck the
        // commit path, so we override only the displayed value via a separate
        // state that's reset as soon as value prop catches up.
        setPreviewText(formatted);
        const loc = place.geometry?.location;
        const hint: PlacesHint | undefined = loc
          ? { lat: loc.lat(), lng: loc.lng(), place_id: place.place_id }
          : undefined;
        void runValidation(formatted, hint);
      });

      return () => {
        listener.remove();
        autocompleteRef.current = null;
      };
    }, [isLoaded, elementRef, allowGeneral]);

    const handleBlur = () => {
      const trimmed = text.trim();
      const initialFormatted = (initial?.formatted ?? "").trim();
      // User cleared a field that previously had a value. Emit an empty
      // AddressValue so the parent's dirty check fires and the Save
      // button appears — without this onChange the clear is invisible
      // to the outer form.
      if (!trimmed && initialFormatted) {
        onChangeRef.current({ formatted: "", validated: false });
        return;
      }
      if (!trimmed) return;
      if (trimmed === initial?.formatted) return;
      // User typed without picking a suggestion — still send it through the
      // validator so the value gets structured/geocoded.
      void runValidation(trimmed);
    };

    useImperativeHandle(
      ref,
      (): AddressInputHandle => ({
        flushPending: async () => {
          const inFlight = pendingPromiseRef.current;
          if (inFlight) {
            return inFlight;
          }
          const trimmed = textRef.current.trim();
          if (!trimmed) return null;
          if (trimmed === initialFormattedRef.current) return null;
          return runValidation(trimmed);
        },
      })
    );

    const current = addressValueOf(value);
    const hasValue = !!current?.formatted;
    const isUnplaceable = isAddressUnplaceable(current);
    // Show the map only when the committed value has coords AND the input
    // text still matches it. While the user is typing a replacement or has
    // cleared the cell, the old map shouldn't linger.
    const showMap =
      current?.lat != null &&
      current?.lng != null &&
      isLoaded &&
      text.trim() === (current.formatted ?? "").trim() &&
      text.trim().length > 0;
    // `GoogleMap` recentres whenever `center` is a new object, and the
    // mousedown that starts a drag blurs the input and re-renders this
    // editor, so a centre rebuilt per render cancelled every drag (#13870).
    // Keyed on the numbers, the object changes only when the coordinates do.
    const lat = current?.lat ?? null;
    const lng = current?.lng ?? null;
    const position = useMemo<google.maps.LatLngLiteral | null>(
      () => (lat !== null && lng !== null ? { lat, lng } : null),
      [lat, lng]
    );

    return (
      <div className={`w-full ${className}`}>
        <div className="relative">
          <input
            ref={elementRef}
            type="text"
            value={previewText ?? text}
            onChange={(e) => {
              setPreviewText(null);
              setText(e.target.value);
            }}
            onBlur={handleBlur}
            placeholder={placeholder}
            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)]"
            autoComplete="off"
          />
          {hasValue && !pending && isUnplaceable && (
            // Only surface a status glyph when something is wrong — a green
            // check on every validated address is visual noise since that's
            // the expected state. The amber warning flags an address a lookup
            // could not place (#13126).
            <Tooltip title={UNPLACEABLE_ADDRESS_TOOLTIP} arrow>
              <span
                role="img"
                aria-label={UNPLACEABLE_ADDRESS_TOOLTIP}
                className="absolute inset-y-0 right-2 flex items-center cursor-help"
              >
                <WarningIcon
                  size={16}
                  weight="fill"
                  className="text-amber-500"
                />
              </span>
            </Tooltip>
          )}
        </div>
        {pending && (
          <p className="mt-1 text-xs text-gray-500">Validating address…</p>
        )}
        {showMap && position && (
          <GoogleMap
            mapContainerStyle={MAP_STYLE}
            center={position}
            zoom={ADDRESS_MAP_ZOOM}
            options={ADDRESS_MAP_OPTIONS}
          >
            <MarkerF position={position} />
          </GoogleMap>
        )}
      </div>
    );
  }
);

AddressInput.displayName = "AddressInput";

export default AddressInput;
